import React, { useState, useEffect } from 'react';
import { AxiosError } from 'axios';
import { Modal, ModalHeader, ModalContent, ModalFooter } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Icon } from '@/shared/icons';
import { useCards, useCredits } from '../hooks';
import { CardFormFields, emptyCardForm } from './CardFormFields';
import type { CardFormData } from './CardFormFields';
import type { CompanyCardResponse } from '../types';
import { CompanyEditDrawer } from '@/modules/settings/company/CompanyEditDrawer';

interface SubscriptionInfo {
  paymentMethod: 'credit_card' | 'pix';
  status: string;
  pendingPaymentId?: string;
}

interface ChangePaymentMethodModalProps {
  isOpen: boolean;
  onClose: () => void;
  subscription: SubscriptionInfo;
}

/**
 * Modal de troca de forma de pagamento — Modal shared (F72).
 */
export const ChangePaymentMethodModal: React.FC<ChangePaymentMethodModalProps> = ({
  isOpen,
  onClose,
  subscription,
}) => {
  const { cards, isLoading: isLoadingCards } = useCards(isOpen);
  const { changePaymentMethodAsync, isChangingPaymentMethod } = useCredits();

  const [selectedCardIndex, setSelectedCardIndex] = useState<number | null>(null);
  const [showNewCard, setShowNewCard] = useState(false);
  const [newCard, setNewCard] = useState<CardFormData>({ ...emptyCardForm });
  const [companyDrawerOpen, setCompanyDrawerOpen] = useState(false);

  // Determine target method (opposite of current)
  const currentMethod = subscription.paymentMethod;
  const targetMethod = currentMethod === 'credit_card' ? 'pix' : 'credit_card';

  const hasPendingPix =
    subscription.status === 'pending' && !!subscription.pendingPaymentId;

  // Auto-select default card when switching to card
  useEffect(() => {
    if (isOpen && targetMethod === 'credit_card' && cards && cards.length > 0 && selectedCardIndex === null) {
      const defaultIdx = cards.findIndex((c: CompanyCardResponse) => c.isDefault);
      setSelectedCardIndex(defaultIdx >= 0 ? defaultIdx : 0);
    }
  }, [isOpen, cards, selectedCardIndex, targetMethod]);

  // Reset state when modal closes (inclui filho CompanyEditDrawer — ciclo pai→filho)
  useEffect(() => {
    if (!isOpen) {
      setSelectedCardIndex(null);
      setShowNewCard(false);
      setNewCard({ ...emptyCardForm });
      setCompanyDrawerOpen(false);
    }
  }, [isOpen]);

  const canConfirm = (): boolean => {
    if (targetMethod === 'pix') return true;
    // Switching to card
    if (showNewCard) {
      return !!(
        newCard.holderName.trim() &&
        newCard.number.length === 16 &&
        newCard.expiryMonth.length === 2 &&
        newCard.expiryYear.length === 2 &&
        newCard.cvv.length === 3
      );
    }
    return selectedCardIndex !== null;
  };

  const handleConfirm = async () => {
    try {
      const currentCentury = Math.floor(new Date().getFullYear() / 100) * 100;
      await changePaymentMethodAsync({
        paymentMethod: targetMethod,
        ...(targetMethod === 'credit_card' && !showNewCard && selectedCardIndex !== null
          ? { cardIndex: selectedCardIndex }
          : {}),
        ...(targetMethod === 'credit_card' && showNewCard
          ? {
              newCard: {
                number: newCard.number,
                cvv: newCard.cvv,
                expiryMonth: parseInt(newCard.expiryMonth, 10),
                expiryYear: currentCentury + parseInt(newCard.expiryYear, 10),
                holderName: newCard.holderName,
                nickname: newCard.nickname || undefined,
                setAsDefault: true,
              },
            }
          : {}),
      });
      onClose();
    } catch (error) {
      if (error instanceof AxiosError) {
        const msg = error.response?.data?.message || error.response?.data?.error || '';
        if (typeof msg === 'string' && (msg.includes('CPF') || msg.includes('CNPJ'))) {
          setCompanyDrawerOpen(true);
        }
      }
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} maxWidth="lg">
        <div data-testid="change-payment-method-modal">
          <ModalHeader title="Trocar Forma de Pagamento" onClose={isChangingPaymentMethod ? undefined : onClose}>
            <span className="text-sm truncate" style={{ color: '#6b7280' }}>
              Atual: {currentMethod === 'credit_card' ? 'Cartão de crédito' : 'PIX'}
            </span>
          </ModalHeader>

          <ModalContent className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto" padding={false}>
            {targetMethod === 'pix' ? (
              /* Switching to PIX */
              <div
                className="flex items-start gap-3 p-4 rounded-lg"
                style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}
              >
                <Icon name="info" className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: '#16a34a' }} />
                <p className="text-sm" style={{ color: '#15803d' }}>
                  A cobrança via PIX será gerada na próxima renovação
                </p>
              </div>
            ) : (
              /* Switching to Card */
              <div className="space-y-4">
                {/* Warning/info based on pending PIX status */}
                {hasPendingPix ? (
                  <div
                    className="flex items-start gap-3 p-4 rounded-lg"
                    style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a' }}
                  >
                    <Icon name="alert-triangle" className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: '#d97706' }} />
                    <p className="text-sm" style={{ color: '#92400e' }}>
                      O pagamento PIX pendente sera cancelado e o cartao selecionado sera cobrado imediatamente
                    </p>
                  </div>
                ) : (
                  <div
                    className="flex items-start gap-3 p-4 rounded-lg"
                    style={{ backgroundColor: '#f0f9ff', border: '1px solid #bae6fd' }}
                  >
                    <Icon name="info" className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: '#0284c7' }} />
                    <p className="text-sm" style={{ color: '#075985' }}>
                      A alteração será aplicada na próxima renovação
                    </p>
                  </div>
                )}

                {/* Card selection */}
                <div className="space-y-3">
                  {isLoadingCards ? (
                    <div className="animate-pulse space-y-2">
                      {[1, 2].map((i) => (
                        <div
                          key={i}
                          className="h-14 rounded-lg"
                          style={{ backgroundColor: '#f3f4f6' }}
                        />
                      ))}
                    </div>
                  ) : (
                    <>
                      {/* Saved cards */}
                      {cards && cards.length > 0 && !showNewCard && (
                        <div className="space-y-2">
                          {cards.map((card: CompanyCardResponse, idx: number) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => setSelectedCardIndex(idx)}
                              className="w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors"
                              style={{
                                backgroundColor: selectedCardIndex === idx ? '#f3e8ff' : '#f9fafb',
                                border: `2px solid ${selectedCardIndex === idx ? '#8b5cf6' : '#e5e7eb'}`,
                              }}
                              data-testid={`change-payment-card-${idx}`}
                            >
                              <Icon name="credit-card" className="h-5 w-5 flex-shrink-0" style={{ color: '#6b7280' }} />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium" style={{ color: '#1e293b' }}>
                                  {card.brand} **** {card.maskedNumber.slice(-4)}
                                </p>
                                <p className="text-xs" style={{ color: '#9ca3af' }}>
                                  {card.holderName} - {String(card.expiryMonth).padStart(2, '0')}/{card.expiryYear}
                                </p>
                              </div>
                              {card.isDefault && (
                                <span
                                  className="text-xs px-2 py-0.5 rounded-full"
                                  style={{ backgroundColor: '#f3e8ff', color: '#7c3aed' }}
                                >
                                  Padrao
                                </span>
                              )}
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Toggle add new */}
                      {!showNewCard ? (
                        <button
                          type="button"
                          onClick={() => {
                            setShowNewCard(true);
                            setSelectedCardIndex(null);
                          }}
                          className="w-full flex items-center gap-2 p-3 rounded-lg text-sm transition-colors"
                          style={{
                            border: '2px dashed #e5e7eb',
                            color: '#6b7280',
                          }}
                          data-testid="change-payment-add-new-card"
                        >
                          <Icon name="plus" className="h-4 w-4" />
                          Adicionar novo cartao
                        </button>
                      ) : (
                        /* New card form */
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium" style={{ color: '#1e293b' }}>
                              Novo cartao
                            </span>
                            {cards && cards.length > 0 && (
                              <button
                                type="button"
                                onClick={() => {
                                  setShowNewCard(false);
                                  setNewCard({ ...emptyCardForm });
                                  const defaultIdx = cards.findIndex((c: CompanyCardResponse) => c.isDefault);
                                  setSelectedCardIndex(defaultIdx >= 0 ? defaultIdx : 0);
                                }}
                                className="text-xs"
                                style={{ color: '#8b5cf6' }}
                              >
                                Usar cartao salvo
                              </button>
                            )}
                          </div>
                          <CardFormFields card={newCard} onChange={setNewCard} />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </ModalContent>

          <ModalFooter>
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={isChangingPaymentMethod}
              data-testid="change-payment-cancel"
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirm}
              disabled={!canConfirm() || isChangingPaymentMethod}
              data-testid="change-payment-submit"
            >
              {isChangingPaymentMethod ? 'Processando...' : 'Confirmar'}
            </Button>
          </ModalFooter>
        </div>
      </Modal>

      <CompanyEditDrawer
        isOpen={companyDrawerOpen}
        onClose={() => setCompanyDrawerOpen(false)}
      />
    </>
  );
};

ChangePaymentMethodModal.displayName = 'ChangePaymentMethodModal';
